// CSR-2100 — a COI request's carrier wait: whether it is in one, and how long
// it has been there.
//
// This lives in `lib` rather than beside the table that renders it because the
// data layer needs the same answer: the list query polls while a wait is on
// screen, and a page importing from `lib` cannot be imported back by it.
//
// One function decides, and everything else asks it. Two copies of "is it
// waiting" is how a row ends up calling itself stuck in one cell and healthy in
// the next.

/** The COI service splits the wait in two — `awaiting_carrier_data` before the
 *  request is approved (asset verification) and `awaiting_carrier_send` during
 *  the parked pre-send refresh. It keeps them apart because its send guard
 *  depends on the difference; nobody reading the screen does. */
export function isAwaitingCarrier(status: string): boolean {
  return (
    status === "awaiting_carrier_data" || status === "awaiting_carrier_send"
  );
}

/** The longest legitimate wait is the COI service's ~3-minute verification cap
 *  plus its 6-minute park. Past this, nothing is coming. */
const CARRIER_WAIT_LIMIT_MS = 10 * 60 * 1000;

/** Ordinary clock drift between hosts — not a reason to distrust a timestamp. */
const CARRIER_WAIT_SKEW_MS = 60 * 1000;

export interface CarrierWaitDisplay {
  label: string;
  tooltip?: string;
}

/**
 * Both readings, from one place, because the risk runs in both directions:
 * inside the window, calling it stuck sends someone to investigate a healthy
 * request; past it, calling it a wait hides the one case a person needs to see.
 *
 * The elapsed time is shown either way — it is what lets someone tell the two
 * apart at a glance.
 */
export function describeCarrierWait(
  updatedAt: Date | string | null | undefined,
  now: number = Date.now(),
): CarrierWaitDisplay {
  const since = updatedAt ? new Date(updatedAt).getTime() : Number.NaN;
  const elapsedMs = Number.isNaN(since) ? Number.NaN : now - since;
  // Without a usable timestamp there is no deadline to judge against, and the
  // reassuring reading would then never expire — a stranded request would say
  // "no action needed" forever, which is the exact failure this exists to
  // prevent. Unknown is treated as actionable. A timestamp slightly ahead of
  // this clock is ordinary drift and still counts as "just started"; one far
  // ahead is a clock we cannot use.
  const unusable =
    Number.isNaN(elapsedMs) || elapsedMs < -CARRIER_WAIT_SKEW_MS;
  if (unusable) {
    return {
      label: "Stuck — needs investigation (wait start unknown)",
      tooltip:
        "This request is waiting on a carrier, but its timestamp is missing or unusable, so there is no way to tell how long. Treat it as stalled.",
    };
  }
  const minutes = Math.max(0, Math.floor(elapsedMs / 60_000));
  const elapsed = minutes < 1 ? "under a minute" : `${minutes} min`;
  if (elapsedMs > CARRIER_WAIT_LIMIT_MS) {
    return {
      label: `Stuck — needs investigation (waiting ${elapsed})`,
      tooltip:
        "A carrier fetch should finish within about ten minutes. This one has not, so the request will not move on its own.",
    };
  }
  return {
    label: `Waiting on carrier (${elapsed})`,
    tooltip:
      "The Data Center is fetching current policy data. No action needed.",
  };
}

/**
 * Is this request sitting in a carrier wait with nothing else already decided
 * about it?
 *
 * Status alone is not enough. `deriveReasonDisplay` gives `notSentReason` and
 * `lastApprovalError` precedence over everything, so a row can hold a
 * carrier-wait status while the reason column beside it says "Send Failed" —
 * and an amber "waiting" icon next to that has the row contradicting itself.
 *
 * Deliberately time-free: this is "not sent YET", which stays true however long
 * the wait has run. Whether it is still coming is `describeCarrierWait`'s
 * question, and keeping it out of here is what stops a clock leaking into the
 * table.
 */
export function isCarrierWaitPending(request: {
  status: string;
  notSentReason?: string | null;
  lastApprovalError?: string | null;
}): boolean {
  if (!isAwaitingCarrier(request.status)) return false;
  return !request.notSentReason && !request.lastApprovalError;
}
